import { useEffect, useState } from "react"
import { Check, X, AlertTriangle } from "lucide-react"

/**
 * Globaler Toast — minimalistisch, ohne Context.
 * Nutzung: `toast("Erfolgreich gesendet")` oder `toast("Fehler", "error")`.
 * Component `<Toaster/>` muss einmal ins App-Root.
 */
type ToastKind = "success" | "error" | "warn"
/** Optionaler Knopf im Toast — fuer „Rueckgaengig" nach einer Aktion, die
 *  sonst nur durch Suchen wieder einzufangen waere (Schritt H). */
export type ToastAction = { label: string; onClick: () => void }
type ToastMsg = { id: number; text: string; kind: ToastKind; action?: ToastAction }

let nextId = 1
const listeners = new Set<(msgs: ToastMsg[]) => void>()
let current: ToastMsg[] = []

function entfernen(id: number) {
  current = current.filter((m) => m.id !== id)
  listeners.forEach((fn) => fn(current))
}

function push(msg: ToastMsg, dauerMs: number) {
  current = [...current, msg]
  listeners.forEach((fn) => fn(current))
  setTimeout(() => entfernen(msg.id), dauerMs)
}

export function toast(text: string, kind: ToastKind = "success", action?: ToastAction) {
  // Mit Knopf laenger stehen lassen — vier Sekunden reichen nicht, um einen
  // Fehlgriff zu bemerken und zu klicken.
  push({ id: nextId++, text, kind, action }, action ? 10000 : 4000)
}

// Global-Zugriff für non-React Context
;(window as any).toast = toast

export function Toaster() {
  const [msgs, setMsgs] = useState<ToastMsg[]>([])

  useEffect(() => {
    listeners.add(setMsgs)
    return () => { listeners.delete(setMsgs) }
  }, [])

  if (msgs.length === 0) return null

  return (
    <div
      style={{
        position: "fixed", top: 20, right: 20, zIndex: 10000,
        display: "flex", flexDirection: "column", gap: 8,
        pointerEvents: "none",
      }}
    >
      {msgs.map((m) => {
        const style = m.kind === "success"
          ? { bg: "#10b981", fg: "#fff", icon: <Check className="w-4 h-4" /> }
          : m.kind === "error"
          ? { bg: "#ef4444", fg: "#fff", icon: <X className="w-4 h-4" /> }
          : { bg: "#f59e0b", fg: "#fff", icon: <AlertTriangle className="w-4 h-4" /> }
        return (
          <div
            key={m.id}
            style={{
              background: style.bg, color: style.fg,
              padding: "10px 14px", borderRadius: 8,
              boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
              fontSize: 13, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 8,
              pointerEvents: "auto",
              maxWidth: 420,
            }}
          >
            {style.icon}
            <span>{m.text}</span>
            {m.action && (
              <button
                type="button"
                onClick={() => { m.action?.onClick(); entfernen(m.id) }}
                style={{
                  marginLeft: 4, background: "rgba(255,255,255,0.22)",
                  color: "inherit", border: 0, borderRadius: 5,
                  padding: "3px 9px", fontSize: 12, fontWeight: 600,
                  cursor: "pointer", whiteSpace: "nowrap",
                }}
              >
                {m.action.label}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
